/* Lamisah design layer — shared behaviour for the lm-* sections.
   Drawers, header scroll states, menu panels, live search, carousels,
   tabs, stacked sliders, the vertical highlight, footer accordions and
   the cart count. No dependencies. */
(function () {
  'use strict';

  var LM = (window.LM = window.LM || {});
  var doc = document;
  var root = doc.documentElement;

  function qs(sel, el) { return (el || doc).querySelector(sel); }
  function qsa(sel, el) { return Array.prototype.slice.call((el || doc).querySelectorAll(sel)); }
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  LM.qs = qs;
  LM.qsa = qsa;
  LM.debounce = debounce;
  LM.esc = esc;

  /* ---------------- money ---------------- */
  LM.formatMoney = function (cents, format, trim) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    format = format || LM.moneyFormat || '{{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(n, dec, th, dc) {
      dec = dec == null ? 2 : dec;
      th = th == null ? ',' : th;
      dc = dc == null ? '.' : dc;
      if (n == null || isNaN(n)) return '0';
      var parts = (n / 100).toFixed(dec).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + th);
      return whole + (parts[1] ? dc + parts[1] : '');
    }
    var m = format.match(re);
    var name = m ? m[1] : 'amount';
    var v;
    switch (name) {
      case 'amount_no_decimals': v = fmt(cents, 0); break;
      case 'amount_with_comma_separator': v = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': v = fmt(cents, 2, ' ', ','); break;
      default: v = fmt(cents, 2);
    }
    if (trim) v = v.replace(/[.,]00$/, '');
    return format.replace(re, v).replace(/<[^>]*>/g, '');
  };

  /* ---------------- scroll lock ---------------- */
  var locks = 0;
  LM.lock = function () { locks += 1; root.classList.add('lm-locked'); };
  LM.unlock = function () {
    locks = Math.max(0, locks - 1);
    if (!locks) root.classList.remove('lm-locked');
  };

  /* ---------------- drawers ---------------- */
  var lastTrigger = null;
  LM.openDrawer = function (id, trigger) {
    var d = typeof id === 'string' ? doc.getElementById(id) : id;
    if (!d || d.classList.contains('is-open')) return;
    qsa('.lm-drawer.is-open').forEach(function (o) { LM.closeDrawer(o, true); });
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    LM.lock();
    lastTrigger = trigger || null;
    qsa('[data-lm-open="' + d.id + '"]').forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
    var target = qs('[data-lm-search-input]', d) || qs('.lm-drawer__panel', d);
    setTimeout(function () {
      try { target.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }, 80);
    d.dispatchEvent(new CustomEvent('lm:open', { bubbles: true }));
  };
  LM.closeDrawer = function (d, silent) {
    if (typeof d === 'string') d = doc.getElementById(d);
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    LM.unlock();
    qsa('[data-lm-open="' + d.id + '"]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    if (!silent && lastTrigger && doc.contains(lastTrigger)) {
      try { lastTrigger.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    lastTrigger = null;
    d.dispatchEvent(new CustomEvent('lm:close', { bubbles: true }));
  };

  doc.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-lm-open]');
    if (opener) {
      e.preventDefault();
      LM.openDrawer(opener.getAttribute('data-lm-open'), opener);
      return;
    }
    var closer = e.target.closest('[data-lm-close]');
    if (closer) {
      var d = closer.closest('.lm-drawer');
      if (d) { e.preventDefault(); LM.closeDrawer(d); }
    }
  });
  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') qsa('.lm-drawer.is-open').forEach(function (d) { LM.closeDrawer(d); });
  });

  /* ---------------- header ---------------- */
  function initHeader(h) {
    if (h.__lmHeader) return;
    h.__lmHeader = true;
    var hide = h.getAttribute('data-hide-on-scroll') === 'true';
    var lastY = window.pageYOffset || 0;
    var ticking = false;

    function measure() {
      if (h.classList.contains('is-solid')) return;
      var hh = h.offsetHeight;
      if (hh) root.style.setProperty('--lm-hh', hh + 'px');
    }
    function update() {
      ticking = false;
      var y = window.pageYOffset || 0;
      var solid = y > 10;
      if (solid && !h.classList.contains('is-solid')) {
        h.classList.add('is-solid');
      } else if (!solid && h.classList.contains('is-solid')) {
        h.classList.remove('is-solid');
        measure();
      }
      if (hide && !root.classList.contains('lm-locked')) {
        var delta = y - lastY;
        if (delta > 4 && y > h.offsetHeight + 120) h.classList.add('is-hidden');
        else if (delta < -4 || y <= 10) h.classList.remove('is-hidden');
      }
      lastY = y;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', debounce(measure, 150));
    measure();
    update();
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(measure);
  }

  /* ---------------- menu drawer panels ---------------- */
  function resetMenu(body) {
    body.classList.remove('is-sub-open');
    qsa('[data-lm-sub-panel]', body).forEach(function (p) { p.classList.remove('is-open'); });
    qsa('[data-lm-sub]', body).forEach(function (b) {
      b.classList.remove('is-active');
      b.setAttribute('aria-expanded', 'false');
    });
  }
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lm-sub]');
    if (btn) {
      var body = btn.closest('[data-lm-menu-body]');
      var panel = doc.getElementById(btn.getAttribute('data-lm-sub'));
      if (!body || !panel) return;
      qsa('[data-lm-sub-panel]', body).forEach(function (p) { p.classList.toggle('is-open', p === panel); });
      qsa('[data-lm-sub]', body).forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      body.classList.add('is-sub-open');
      if (!window.matchMedia('(min-width: 992px)').matches) body.scrollTop = 0;
      return;
    }
    var back = e.target.closest('[data-lm-back]');
    if (back) {
      var b2 = back.closest('[data-lm-menu-body]');
      if (b2) resetMenu(b2);
    }
  });
  doc.addEventListener('lm:close', function (e) {
    var body = e.target && e.target.querySelector && e.target.querySelector('[data-lm-menu-body]');
    if (body) setTimeout(function () { resetMenu(body); }, 520);
    closeCountry(e.target);
  });

  /* ---------------- menu tabs (each tab swaps the list) ---------------- */
  doc.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-lm-menu-tab]');
    if (!tab) return;
    e.preventDefault();
    var menu = tab.closest('.lm-menu') || doc;
    var target = tab.getAttribute('data-lm-menu-tab');
    qsa('[data-lm-menu-tab]', menu).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    qsa('[data-lm-menu-pane]', menu).forEach(function (p) { p.classList.toggle('is-active', p.id === target); });
    var body = qs('[data-lm-menu-body]', menu);
    if (body) { resetMenu(body); body.scrollTop = 0; }
  });

  /* ---------------- country / currency list ---------------- */
  function closeCountry(scope) {
    qsa('[data-lm-country-toggle][aria-expanded="true"]', scope && scope.querySelectorAll ? scope : doc).forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
      var list = doc.getElementById(t.getAttribute('aria-controls'));
      if (list) list.hidden = true;
    });
  }
  doc.addEventListener('click', function (e) {
    var t = e.target.closest('[data-lm-country-toggle]');
    if (t) {
      e.preventDefault();
      var open = t.getAttribute('aria-expanded') !== 'true';
      closeCountry(doc);
      if (open) {
        t.setAttribute('aria-expanded', 'true');
        var list = doc.getElementById(t.getAttribute('aria-controls'));
        if (list) list.hidden = false;
      }
      return;
    }
    if (!e.target.closest('.lm-menu__country-list')) closeCountry(doc);
  });
  doc.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeCountry(doc); });

  /* ---------------- live search ---------------- */
  function initSearch(d) {
    if (d.__lmSearch) return;
    d.__lmSearch = true;
    var input = qs('[data-lm-search-input]', d);
    var results = qs('[data-lm-search-results]', d);
    var fallback = qs('[data-lm-search-default]', d);
    if (!input || !results) return;
    var trim = d.getAttribute('data-trim') === 'true';
    var strings = LM.searchStrings || { loading: 'Searching…', empty: 'No results found', all: 'View all results' };
    var controller = null;

    function cardHtml(p) {
      var img = (p.featured_image && p.featured_image.url) || p.image || '';
      var src = img ? img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=400' : '';
      var now = Math.round(parseFloat(p.price || 0) * 100);
      var was = Math.round(parseFloat(p.compare_at_price_max || 0) * 100);
      var sale = was > now;
      var ratio = getComputedStyle(root).getPropertyValue('--lm-card-ratio').trim() || '2 / 3';
      var card = LM.card || {};
      var pct = sale ? Math.round(((was - now) * 100) / was) : 0;
      var off = sale && card.showOff !== false && pct > 0
        ? '<span class="lm-card__off">' + pct + '% ' + esc(card.off || 'Off') + '</span>' : '';
      var nowHtml = esc(LM.formatMoney(now, LM.moneyFormat, trim));
      var price = sale
        ? '<s class="lm-card__was"><span class="lm-vh">' + esc(card.regular || '') + '</span>' + esc(LM.formatMoney(was, LM.moneyFormat, trim)) + '</s>' +
          '<span class="lm-card__now is-sale"><span class="lm-vh">' + esc(card.sale || '') + '</span>' + nowHtml + '</span>'
        : '<span class="lm-card__now">' + nowHtml + '</span>';
      return '<div class="lm-card">' +
        '<div class="lm-card__media" style="--ratio:' + esc(ratio) + '">' +
          '<a class="lm-card__img-link" href="' + esc(p.url) + '">' +
            (src ? '<img class="lm-card__img" src="' + esc(src) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') +
          '</a>' + off +
        '</div>' +
        '<div class="lm-card__body">' +
          '<div class="lm-card__top"><a class="lm-card__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a></div>' +
          '<p class="lm-card__price">' + price + '</p>' +
        '</div>' +
      '</div>';
    }

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) {
        results.hidden = true;
        if (fallback) fallback.hidden = false;
        return;
      }
      results.hidden = false;
      if (fallback) fallback.hidden = true;
      results.innerHTML = '<p class="lm-search__status">' + esc(strings.loading) + '</p>';
      if (controller && controller.abort) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var base = LM.suggestUrl || '/search/suggest';
      var url = base + '.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
      fetch(url, { signal: controller ? controller.signal : undefined, headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var items = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          var allUrl = (LM.searchUrl || '/search') + '?type=product&q=' + encodeURIComponent(q);
          if (!items.length) {
            results.innerHTML = '<p class="lm-search__status">' + esc(strings.empty) + '</p>' +
              '<a class="lm-search__all" href="' + esc(allUrl) + '">' + esc(strings.all) + '</a>';
            return;
          }
          results.innerHTML = '<div class="lm-search__grid">' + items.map(cardHtml).join('') + '</div>' +
            '<a class="lm-search__all" href="' + esc(allUrl) + '">' + esc(strings.all) + '</a>';
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          results.innerHTML = '<p class="lm-search__status">' + esc(strings.empty) + '</p>';
        });
    }, 260);

    input.addEventListener('input', run);
  }

  /* ---------------- carousel (scroll-snap) ---------------- */
  function Carousel(el) {
    this.el = el;
    this.track = qs('[data-lm-track]', el);
    this.prevBtn = qs('[data-lm-prev]', el);
    this.nextBtn = qs('[data-lm-next]', el);
    this.progress = qs('[data-lm-progress]', el);
    this.loop = el.getAttribute('data-loop') !== 'false';
    this.autoplay = parseInt(el.getAttribute('data-autoplay') || '0', 10);
    this.paused = false;
    this.visible = true;
    this.segments = 0;
    var self = this;
    if (!this.track) return;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.go(self.index() - 1); self.hold(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.go(self.index() + 1); self.hold(); });
    this.track.addEventListener('scroll', debounce(function () { self.paint(); }, 60), { passive: true });
    window.addEventListener('resize', debounce(function () { self.refresh(); }, 150));
    el.addEventListener('pointerenter', function () { self.paused = true; });
    el.addEventListener('pointerleave', function () { self.paused = false; });
    el.addEventListener('focusin', function () { self.paused = true; });
    el.addEventListener('focusout', function () { self.paused = false; });
    this.track.addEventListener('touchstart', function () { self.hold(); }, { passive: true });
    if (this.autoplay > 0) {
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.visible = entries[0].isIntersecting;
        }, { threshold: 0.3 }).observe(el);
      }
      this.timer = setInterval(function () {
        if (!self.paused && self.visible && !doc.hidden && !root.classList.contains('lm-locked')) self.go(self.index() + 1);
      }, this.autoplay);
    }
    this.refresh();
  }
  Carousel.prototype.hold = function () {
    var self = this;
    this.paused = true;
    clearTimeout(this.holdT);
    this.holdT = setTimeout(function () { self.paused = false; }, 6000);
  };
  Carousel.prototype.step = function () {
    var kids = this.track.children;
    if (!kids.length) return this.track.clientWidth || 1;
    if (kids.length > 1) return Math.abs(kids[1].offsetLeft - kids[0].offsetLeft) || kids[0].offsetWidth || 1;
    return kids[0].offsetWidth || 1;
  };
  Carousel.prototype.pages = function () {
    var step = this.step();
    var extra = this.track.scrollWidth - this.track.clientWidth;
    if (extra <= 2) return 1;
    return Math.round(extra / step) + 1;
  };
  Carousel.prototype.index = function () {
    return Math.round(this.track.scrollLeft / this.step());
  };
  Carousel.prototype.go = function (i, instant) {
    var pages = this.pages();
    if (pages <= 1) return;
    if (this.loop) {
      if (i >= pages) i = 0;
      if (i < 0) i = pages - 1;
    } else {
      i = Math.max(0, Math.min(pages - 1, i));
    }
    var left = Math.min(i * this.step(), this.track.scrollWidth - this.track.clientWidth);
    this.track.scrollTo({ left: left, behavior: instant ? 'auto' : 'smooth' });
  };
  Carousel.prototype.paint = function () {
    var pages = this.pages();
    var i = Math.min(pages - 1, this.index());
    if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2) i = pages - 1;
    if (this.progress) {
      if (this.segments !== pages) {
        this.segments = pages;
        var html = '';
        for (var k = 0; k < pages; k++) html += '<span class="lm-progress__seg"></span>';
        this.progress.innerHTML = html;
      }
      this.progress.hidden = pages <= 1;
      qsa('.lm-progress__seg', this.progress).forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
    }
    var none = pages <= 1;
    if (this.prevBtn) {
      this.prevBtn.hidden = none;
      this.prevBtn.disabled = !this.loop && i <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.hidden = none;
      this.nextBtn.disabled = !this.loop && i >= pages - 1;
    }
    this.el.setAttribute('data-index', i);
  };
  Carousel.prototype.refresh = function () { this.segments = 0; this.paint(); };
  LM.Carousel = Carousel;

  /* ---------------- tabs ---------------- */
  function initTabs(el) {
    if (el.__lmTabs) return;
    el.__lmTabs = true;
    var tabs = qsa('[data-lm-tab]', el);
    var panels = qsa('[data-lm-panel]', el);
    function activate(tab) {
      var id = tab.getAttribute('data-lm-tab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-lm-panel') === id;
        p.hidden = !on;
        p.classList.toggle('is-active', on);
        if (on) qsa('[data-lm-carousel]', p).forEach(function (c) { if (c.__lmc) c.__lmc.refresh(); });
      });
    }
    tabs.forEach(function (t) {
      t.addEventListener('click', function () { activate(t); });
      t.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(t);
        if (e.key === 'ArrowRight') { e.preventDefault(); tabs[(i + 1) % tabs.length].focus(); activate(tabs[(i + 1) % tabs.length]); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); tabs[(i - 1 + tabs.length) % tabs.length].focus(); activate(tabs[(i - 1 + tabs.length) % tabs.length]); }
      });
    });
  }

  /* ---------------- stacked slider (testimonials) ---------------- */
  function initStack(el) {
    if (el.__lmStack) return;
    el.__lmStack = true;
    var slides = qsa('[data-lm-stack-slide]', el);
    if (!slides.length) return;
    var i = 0;
    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) {
        s.classList.toggle('is-active', k === i);
        s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
      });
    }
    qsa('[data-lm-stack-next]', el).forEach(function (b) { b.addEventListener('click', function () { show(i + 1); }); });
    qsa('[data-lm-stack-prev]', el).forEach(function (b) { b.addEventListener('click', function () { show(i - 1); }); });
    var auto = parseInt(el.getAttribute('data-autoplay') || '0', 10);
    if (auto > 0 && slides.length > 1) {
      var paused = false;
      el.addEventListener('pointerenter', function () { paused = true; });
      el.addEventListener('pointerleave', function () { paused = false; });
      setInterval(function () { if (!paused && !doc.hidden) show(i + 1); }, auto);
    }
    show(0);
  }

  /* ---------------- vertical highlight ----------------
     Computers: scroll mode pins the section and changes the slide while the page scrolls.
     Phones (991px and less): the slides sit in a native swipe track (CSS scroll-snap),
     the dots follow the swipe and jump to a slide when tapped. */
  function initHighlight(el) {
    if (el.__lmHl) return;
    el.__lmHl = true;
    var slides = qsa('[data-lm-hl-slide]', el);
    var dots = qsa('[data-lm-hl-dot]', el);
    var track = qs('[data-lm-hl-track]', el);
    var mode = el.getAttribute('data-mode') || 'scroll';
    var phone = window.matchMedia('(max-width: 991px)');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var editor = !!(window.Shopify && window.Shopify.designMode);
    var current = -1;
    var swipe = false;

    /* every slide image loads as the section comes near, so no slide slides in blank */
    var imgs = qsa('.lm-hl__media img', el);
    var eager = function () { imgs.forEach(function (im) { im.loading = 'eager'; }); };
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (list) {
        if (list.some(function (x) { return x.isIntersecting; })) { eager(); io.disconnect(); }
      }, { rootMargin: '800px 0px' });
      io.observe(el);
    } else {
      eager();
    }

    function set(i) {
      if (i === current) return;
      current = i;
      slides.forEach(function (sl, k) {
        sl.classList.toggle('is-active', k === i);
        sl.classList.toggle('is-before', k < i);
        if (swipe) sl.removeAttribute('aria-hidden');
        else sl.setAttribute('aria-hidden', k === i ? 'false' : 'true');
      });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
    }
    if (slides.length < 2) { set(0); return; }

    function trackIndex() {
      if (!track || !track.clientWidth) return 0;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
    }
    function pinnedIndex() {
      var r = el.getBoundingClientRect();
      var total = el.offsetHeight - window.innerHeight;
      var p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      return Math.min(slides.length - 1, Math.floor(p * slides.length * 0.9999));
    }

    var busy = false;
    function frame(fn) {
      return function () {
        if (busy) return;
        busy = true;
        window.requestAnimationFrame(function () { busy = false; fn(); });
      };
    }
    var onPageScroll = frame(function () {
      if (!swipe && el.classList.contains('is-scroll-mode')) set(pinnedIndex());
    });
    var onTrackScroll = frame(function () { if (swipe) set(trackIndex()); });

    function apply() {
      swipe = phone.matches;
      el.classList.toggle('is-swipe', swipe);
      el.classList.toggle('is-scroll-mode', !swipe && mode === 'scroll' && !reduce && !editor);
      current = -1;
      if (swipe) set(trackIndex());
      else if (el.classList.contains('is-scroll-mode')) set(pinnedIndex());
      else set(0);
    }

    window.addEventListener('scroll', onPageScroll, { passive: true });
    window.addEventListener('resize', debounce(function () { if (!swipe) onPageScroll(); }, 150));
    if (track) track.addEventListener('scroll', onTrackScroll, { passive: true });
    if (phone.addEventListener) phone.addEventListener('change', apply);
    else if (phone.addListener) phone.addListener(apply);

    function goTo(k) {
      if (swipe && track) {
        track.scrollTo({ left: k * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      } else if (el.classList.contains('is-scroll-mode')) {
        var total = el.offsetHeight - window.innerHeight;
        var top = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: top + (total * (k + 0.5)) / slides.length, behavior: 'smooth' });
      } else {
        set(k);
      }
    }
    dots.forEach(function (d, k) { d.addEventListener('click', function () { goTo(k); }); });

    var auto = parseInt(el.getAttribute('data-autoplay') || '0', 10);
    if (auto > 0) {
      var held = false;
      el.addEventListener('touchstart', function () { held = true; }, { passive: true });
      el.addEventListener('touchend', function () { held = false; }, { passive: true });
      setInterval(function () {
        if (doc.hidden || held) return;
        if (!swipe && el.classList.contains('is-scroll-mode')) return;
        goTo((current + 1) % slides.length);
      }, auto);
    }
    apply();
  }

  /* ---------------- footer accordions ---------------- */
  doc.addEventListener('click', function (e) {
    var t = e.target.closest('[data-lm-acc-toggle]');
    if (!t) return;
    if (window.matchMedia('(min-width: 769px)').matches) return;
    var col = t.closest('[data-lm-acc]');
    if (!col) return;
    var open = !col.classList.contains('is-open');
    col.classList.toggle('is-open', open);
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ---------------- cart count ---------------- */
  LM.setCartCount = function (n) {
    qsa('[data-lm-cart-count]').forEach(function (el) { el.textContent = n; });
  };
  function watchCart() {
    var src = qs('[data-cd-count]');
    if (!src || src.__lmWatch) return;
    src.__lmWatch = true;
    var read = function () {
      var m = (src.textContent || '').match(/\d+/);
      if (m) LM.setCartCount(m[0]);
    };
    new MutationObserver(read).observe(src, { childList: true, characterData: true, subtree: true });
    read();
  }
  doc.addEventListener('cart:update', function (e) {
    var c = e.detail && (e.detail.resource || e.detail.cart);
    if (c && typeof c.item_count === 'number') LM.setCartCount(c.item_count);
  });

  /* ---------------- boot ---------------- */
  function init(scope) {
    scope = scope || doc;
    qsa('[data-lm-header]', scope).forEach(initHeader);
    qsa('[data-lm-search]', scope).forEach(initSearch);
    qsa('[data-lm-carousel]', scope).forEach(function (el) { if (!el.__lmc) el.__lmc = new Carousel(el); });
    qsa('[data-lm-tabs]', scope).forEach(initTabs);
    qsa('[data-lm-stack]', scope).forEach(initStack);
    qsa('[data-lm-highlight]', scope).forEach(initHighlight);
    watchCart();
  }
  LM.init = init;

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* theme editor */
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
  doc.addEventListener('shopify:section:unload', function () {
    qsa('.lm-drawer.is-open').forEach(function (d) { LM.closeDrawer(d, true); });
  });
  doc.addEventListener('shopify:block:select', function (e) {
    var drawer = e.target.closest && e.target.closest('.lm-drawer');
    if (drawer) LM.openDrawer(drawer);
    var slide = e.target.closest && e.target.closest('[data-lm-carousel] [data-lm-track] > *');
    if (slide) {
      var c = slide.closest('[data-lm-carousel]');
      if (c && c.__lmc) c.__lmc.go(Array.prototype.indexOf.call(slide.parentNode.children, slide), true);
    }
  });
})();
